import './index.css';

import { Bolt } from '@mui/icons-material';
import { Box, Button } from '@mui/material';
import React, { useState } from 'react';

import { Card } from '../Card';
import { ChargePointStatus } from 'utils/AppConsts';
import LineChart from 'components/Charts/Line';
import LoadingAnimation from 'components/LoadingAnimation';
import { LocalizationKeys } from 'assets/localizations/localizationKeys';
import { MTypography } from 'components/MTypography';
import { MapCard } from '../MapCard';
import { SessionDataList } from '../SessionDataList';
import { SessionNumberBoxes } from '../SessionNumberBoxes';
import { SessionPreview } from '../SessionPreview';
import { SessionStatus } from '../SessionStatus';
import { observer } from 'mobx-react';
import { useParams } from 'react-router-dom';
import { useStores } from 'stores/storeInitializer';
import { getSessionCurrency } from 'utils/currency';

export const DetailsPage: React.FC = observer(() => {
    const { id, tenantId } = useParams<{
        id: string;
        tenantId: string;
    }>();
    const { store } = useStores();

    const charger = React.useMemo(() => {
        return store.session?.chargeBox;
    }, [store.session]);

    const isSessionStopped = React.useMemo(() => {
        return !!(store.session?.stop?.totalDurationSecs !== undefined);
    }, [store.session]);

    const status = store.session?.chargeBox?.connectors?.find(
        (c) => c.connectorId === store.session?.connectorId,
    )?.status;

    const stopStatuses: ChargePointStatus[] = [
        ChargePointStatus.CHARGING,
        ChargePointStatus.SUSPENDED_EVSE,
        ChargePointStatus.SUSPENDED_EV,
    ];

    const isStopped = useHasTransactionBeenStopped();

    return (
        (store.session && (
            <React.Fragment>
                {id && (
                    <div
                        style={{
                            flexDirection: 'column',
                            justifyContent: 'flex-start',
                            alignItems: 'center',
                            width: '100%',
                            height: '100%',
                            overflow: 'auto',
                        }}
                    >
                        <SessionStatus session={store.session} />
                        {!isSessionStopped ? (
                            <SessionPreview />
                        ) : (
                            <SessionNumberBoxes session={store.session} />
                        )}
                        {!isSessionStopped &&
                            status &&
                            stopStatuses.includes(status) ? (
                            <Card
                                style={{
                                    marginTop: 30,
                                    marginBottom: 30,
                                }}
                            >
                                <Button
                                    style={{ width: '100%' }}
                                    variant="contained"
                                    color="error"
                                    disabled={
                                        isStopped ||
                                        !store.session
                                            .currentTotalDurationSecs ||
                                        store.session
                                            .currentTotalDurationSecs === 0
                                    }
                                    onClick={async () => {
                                        if (tenantId && store.deviceId) {
                                            await store.stopTransactionOtp(
                                                tenantId,
                                                id,
                                                {
                                                    token: store.token || '',
                                                    deviceID: store.deviceId,
                                                },
                                            );
                                        }
                                    }}
                                >
                                    {store.L(LocalizationKeys.StopTransaction)}
                                </Button>
                            </Card>
                        ) : null}
                        <MapCard
                            charger={charger}
                            chargeBoxId={store.session.chargeBoxID}
                        />
                        <Card
                            style={{
                                marginTop: 20,
                                marginBottom: 20,
                            }}
                        >
                            <Box pt={2}>
                                <MTypography
                                    sx={{ display: 'inline', padding: '1rem' }}
                                    variant="button"
                                    color="text.secondary"
                                >
                                    {LocalizationKeys.SessionDetails}
                                </MTypography>
                            </Box>
                            <SessionDataList
                                session={store.session}
                                charger={charger}
                            />
                        </Card>
                        <Card style={{ marginTop: 20, marginBottom: 20 }}>
                            <LineChart
                                chart={store.ChartViewOfConsumptionsWh as any}
                                title={store.L(LocalizationKeys.Conusmption)}
                                description={store.L(
                                    LocalizationKeys.ConusmptionOverTime,
                                )}
                                icon={{
                                    component: <Bolt />,
                                    color: 'info',
                                }}
                            />
                        </Card>
                        <Card style={{ marginTop: 2, marginBottom: 20 }}>
                            <LineChart
                                chart={store.ChartViewOfCumulativePrice as any}
                                title={store.L(LocalizationKeys.Cost)}
                                description={store.L(
                                    LocalizationKeys.CostOverTime,
                                )}
                                icon={{
                                    component: (
                                        <Box
                                            component="span"
                                            sx={{
                                                fontFamily: 'Roboto, sans-serif',
                                                fontSize: 12,
                                                fontWeight: 700,
                                                lineHeight: 1,
                                            }}
                                        >
                                            {getSessionCurrency(store.session)}
                                        </Box>
                                    ),
                                    color: 'success',
                                }}
                            />
                        </Card>
                    </div>
                )}
            </React.Fragment>
        )) || <LoadingAnimation />
    );
});

const useHasTransactionBeenStopped = () => {
    const [isStopped, setIsStopped] = useState(false);

    const { id, tenantId } = useParams<{
        id: string;
        tenantId: string;
    }>();

    if (!isStopped) {
        const val = localStorage.getItem(`${tenantId}-${id}`);

        if (val === 'stopped') {
            setIsStopped(true);
        }
    }

    return isStopped;
};

export default DetailsPage;
